import React from 'react'
import { Container, Row, Col, Card, Button } from 'react-bootstrap'
import { AccordionSection, AccordionCard } from '../../accordion'
import { LeaseTerm } from '@mmsg/louie'
import ContactUsDialog from '../../dialog/contactUsDialog'
import { useSelector } from 'react-redux'
import Tooltip from '../../tooltip'
import { PrimaryLink } from 'components/styledComponents/link'
import { Tabs } from '@mmsg/louie'
import { Icon } from 'icons'
import {
  capitalizeOnlyFirstLetter,
  toLowerCase,
  formatCurrency,
} from '../../../helpers'
import { applicationRoles } from '../../../constants/actionTypes'

const TakeHomePayTable = ({
  grossSalary,
  preTaxDeduction,
  taxShare,
  otherPreTax,
  netSalary,
  taxMedicare,
  netIncome,
  postTax,
  otherPostTax,
  takeHome,
  fbt,
}) => (
  <>
    <Row className="mx-0 mr-md-3">
      <Col xs={12} md={2} className="text-header px-0">
        Pre-tax
      </Col>
      <Col xs={6} md={5} className="px-0">
        <div className="d-flex ">
          <span className="mr-2">
            <Tooltip
              header="Gross"
              headerText="Gross income"
              text="The total income you earn before PAYG (income tax), minus your superannuation or any other deductions. Think salaries, and even bonuses."
              launchViaTitleText="true"
            />{' '}
            income
          </span>
        </div>
      </Col>
      <Col xs={6} md={5} className="text-right px-0">
        {formatCurrency(grossSalary)}
      </Col>
    </Row>
    <Row className="mx-0 mr-md-3">
      <Col xs={8} md={{ offset: 2 }} className="px-0 col--nobold">
        <div className="d-flex ">
          <span className="mr-2">
            <Tooltip
              header="Pre-tax"
              headerText="Pre-tax novated lease deduction"
              text="The portion of your lease deductions taken before you’ve been taxed. This deduction lowers your overall taxable income, which means you could pay less tax."
              launchViaTitleText="true"
            />{' '}
            novated lease deduction{' '}
          </span>
        </div>
      </Col>
      <Col xs={4} className="text-right px-0">
        {formatCurrency(preTaxDeduction)}
      </Col>
    </Row>
    {taxShare > 0 && (
      <Row className="mx-0 mr-md-3">
        <Col xs={8} md={{ offset: 2 }} className="px-0 col--nobold">
          Tax Share Deduction
        </Col>
        <Col xs={4} className="text-right px-0">
          {formatCurrency(taxShare)}
        </Col>
      </Row>
    )}
    {otherPreTax > 0 && (
      <Row className="mx-0 mr-md-3">
        <Col xs={8} md={{ offset: 2 }} className="px-0 col--nobold">
          Other salary packaging (Pre-tax)
        </Col>
        <Col xs={4} className="text-right px-0">
          {formatCurrency(otherPreTax)}
        </Col>
      </Row>
    )}
    <Row className="font-weight-bold mx-0 mr-md-3">
      <Col
        xs={8}
        md={{ offset: 2 }}
        className="pt-2 px-0 border-top col--nobold"
      >
        Resulting taxable income
      </Col>
      <Col xs={4} className="text-right pt-2 px-0 border-top">
        {formatCurrency(netSalary)}
      </Col>
    </Row>
    <div className="bg-light pb-2 pt-3 mt-3 ">
      {fbt > 0 && (
        <Row className="mx-0 mr-md-3">
          <Col xs={8} md={{ offset: 2 }} className="px-0 col--nobold">
            <div className="d-flex ">
              <span className="mr-2">
                <Tooltip
                  header="FBT"
                  headerText="FBT"
                  text="Fringe Benefits Tax As your novated lease is a benefit your employer provides outside of your salary or wages, it’s subject to FBT. You may be able to offset any FBT liability by using the Employee Contribution Method."
                  launchViaTitleText="true"
                />{' '}
              </span>
            </div>
          </Col>
          <Col xs={4} className="text-right px-0">
            {formatCurrency(fbt)}
          </Col>
        </Row>
      )}
      <Row className="mx-0 mr-md-3">
        <Col xs={8} md={{ offset: 2 }} className="px-0">
          <div className="d-flex ">
            <span className="mr-2 col--nobold">
              <Tooltip
                header="Tax and medicare"
                headerText="Tax and medicare deduction"
                text="A combined deduction of your estimated income tax and resulting Medicare levy. This deduction is collected by your employer."
                launchViaTitleText="true"
              />{' '}
              deduction
            </span>
          </div>
        </Col>
        <Col xs={4} className="text-right px-0">
          {formatCurrency(taxMedicare)}
        </Col>
      </Row>
      <Row className="font-weight-bold mx-0 mr-md-3">
        <Col xs={8} md={{ offset: 2 }} className="pt-2 px-0 border-top">
          <div className="d-flex ">
            <span className="mr-2">
              <Tooltip
                header="Resulting net income"
                text="Your income once all of your deductions (like income tax, superannuation & other salary packaging items) have been taken."
                launchViaTitleText="true"
              />{' '}
            </span>
          </div>
        </Col>
        <Col xs={4} className="text-right pt-2 px-0 border-top">
          {formatCurrency(netIncome)}
        </Col>
      </Row>
    </div>
    <Row className="mx-0 mr-md-3 mt-4">
      <Col xs={12} md={2} className="text-header px-0">
        Post-tax
      </Col>
      <Col xs={6} md={7} className="px-0">
        <div className="d-flex ">
          <span className="mr-2">
            <Tooltip
              header="Post-tax"
              headerText="Post-tax novated lease deduction (ECM)"
              text="We’ll deduct your payments from your salary before and after it gets taxed (your gross income, and your resulting net income). These post-tax deductions could free up money you're currently paying in income tax (and the Medicare levy), which we then put straight towards your lease. This method is called the Employee Contribution Method (ECM)."
              launchViaTitleText="true"
            />{' '}
            novated lease deduction (ECM){' '}
          </span>
        </div>
      </Col>
      <Col xs={6} md={3} className="text-right px-0">
        {formatCurrency(postTax)}
      </Col>
    </Row>
    {otherPostTax > 0 && (
      <Row className="mx-0 mr-md-3">
        <Col xs={8} md={{ offset: 2 }} className="px-0 col--nobold">
          Other salary packaging items
        </Col>
        <Col xs={4} className="text-right px-0">
          {formatCurrency(otherPostTax)}
        </Col>
      </Row>
    )}
    <Row className="font-weight-bold border-top mx-0 pt-2 mt-3">
      <Col xs={8} md={{ offset: 2 }} className="px-0">
        <div className="d-flex ">
          <span className="mr-2">
            <Tooltip
              header="Resulting take home pay"
              text="The income you get to take home after tax and your novated leasing or salary package deductions. This amount doesn’t take into account any HECS/HELP repayments you might have."
              launchViaTitleText="true"
            />
          </span>
        </div>
      </Col>
      <Col xs={4} className="text-right px-0 pr-md-3">
        {formatCurrency(takeHome)}
      </Col>
    </Row>
  </>
)
const SummarySection = (
  {
    data: {
      year,
      make,
      family,
      leaseTermYears,
      leaseTerm,
      payFrequency,
      annualRate,
      carRepaymentPerPay,
      totalPackagePerPay,
      runningCostsPerPay,
      savingsPerPay,
      outOfPocketPerPay,
      brand,
      fuelBudget,
      electricityBudget,
      tyresBudget,
      incidentalsBudget,
      registrationBudget,
      scheduleMaintenanceBudget,
      roadsideAssistanceBudget,
      compInsRenewalBudget,
      takeHomePerPay,
      grossSalaryPerPay,
      grossAnnualSalary,
      preTaxPerPay,
      spPreTaxDeduction,
      otherPreTaxPerPay,
      preTaxDeduction,
      taxSharePerPay,
      spTaxShareDeduction,
      netSalaryPerPay,
      netSalaryPerAnnum,
      taxMedicarePerPay,
      spTaxAndMedicare,
      netIncomePerPay,
      spNetCashSalary,
      postTaxPerPay,
      specm,
      otherPostTaxPerPay,
      postTaxDeduction,
      spCashinhand,
      lifeOfLeaseTaxSaving,
      leaseResidualAmountIncGST,
      userFirstName,
      consultantFirstName,
      vehicleDescription,
      opportunityId,
      vehicleId,
      isGov,
      displayInterest,
      fbtPerPay,
      fbtPerAnnum,
      gstSavingsOnRunningCosts,
      productId,
      fbtMethod,
      vehicleFBTValue,
    },
    applicationRole,
    expandAccordion,
  },
  props
) => {
  const ScrollToEndOfLeaseSection = () => {
    document
      .getElementById('endOfLeaseSection')
      .scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' })
  }
  let estimateID = useSelector(
    (state) => state.individualEstimate.selectedEstimateId
  )
  let leaseTermTxt =
    leaseTerm % 12 === 0 ? leaseTermYears + ' Years' : leaseTerm + ' Months'

  let fbtValuationText = ''
  if (fbtMethod && fbtMethod.toLowerCase() === 'ecm')
    fbtValuationText =
      ' Post-tax novated lease deduction (ECM) is calculated using an FBT Base value of ' +
      formatCurrency(vehicleFBTValue) +
      '.'
  if (fbtMethod && fbtMethod.toLowerCase() === 'fbt')
    fbtValuationText =
      ' FBT is calculated using an FBT Base value of ' +
      formatCurrency(vehicleFBTValue) +
      '.'

  return (
    <div>
      <Container
        fluid
        className="text-dark-black bg-shape container-summary print-page-break-before"
      >
        <Container className="section">
          <h3 className="text-dark-black mb-4">First up, your lease summary</h3>
          <p>
            Let’s breakdown what your novated lease would look like if you
            unlocked the
            {vehicleId && (
              <>
                {' '}
                {year} {make} {family}{' '}
              </>
            )}
            {!vehicleId && <> {vehicleDescription} </>}
            on a {leaseTermTxt} lease.
          </p>
          <div className="font-weight-bold mb-2">
            Your chosen novated lease term
          </div>
          <p className="mb-4">
            Your lease term is still flexible, and designed to reflect how long
            you’d like to keep the car. Adjusting this term will adjust the
            amount you pay {toLowerCase(payFrequency)}, as well as your residual
            at the end of your lease.
          </p>
          <LeaseTerm months={leaseTerm} />
          <br></br>
          <p className="mb-4">
            Want to update this term before you take out your lease? Can do.
            <br />
            {applicationRole &&
              applicationRole !== applicationRoles.CONSULTANT && (
                <>
                  <ContactUsDialog
                    className="link"
                    theme="link"
                    launchText="Simply contact your consultant"
                    inlineText
                  />{' '}
                </>
              )}
            {applicationRole &&
              applicationRole === applicationRoles.CONSULTANT && (
                <>
                  <a className="link text-underline readonly" theme="link">
                    contact your consultant
                  </a>{' '}
                </>
              )}
            for help.
          </p>

          {productId && (
            <div>
              <div className="font-weight-bold mb-2">
                Your chosen lease product type
              </div>
              <p>
                {capitalizeOnlyFirstLetter(
                  productId.toLowerCase().replace('novated lease', '').trim()
                )}
              </p>
            </div>
          )}
        </Container>
        <Container className="section-summary mb-24">
          <Card className="mb-24 card-shadow">
            <Card.Body className="card-body--summary">
              <Row className="your-boxes mb-5">
                <Col xs={12} md={4} className="col--nobold">
                  <h5 className="mb-2">Your total package</h5>
                  <Card body className="your-box">
                    <div>Car finance</div>
                    <div className="text-largest">+</div>
                    <div>On the road budget</div>
                  </Card>
                  <p className="text-small">
                    <strong>Over life of lease</strong>
                    <br />
                    Making your experience on the road smoother with support
                    from the {brand} team
                  </p>
                </Col>
                <Col xs={12} md={4}>
                  <h5 className="mb-2">Potential tax benefits</h5>
                  <Card
                    body
                    className="your-box your-box--benefits tax-benefits"
                  >
                    <div>{formatCurrency(savingsPerPay)}</div>
                  </Card>
                  <p className="text-small">
                    <strong> {payFrequency}</strong>
                    <br />
                    Access ongoing potential tax benefits, exclusive to novated
                    leasing
                  </p>
                </Col>
                <Col xs={12} md={4}>
                  <h5 className="mb-2">Estimated additional out of pocket</h5>
                  <Card body className="your-box your-box--pocket">
                    <div>{formatCurrency(outOfPocketPerPay)}</div>
                  </Card>
                  <p className="text-small">
                    <strong> {payFrequency}</strong>
                    <br />
                    To try and keep your out of pocket cost lower than your
                    total deductions, we put these benefits straight towards
                    your package
                  </p>
                </Col>
              </Row>

              {/* What we'll deduct each fortnight: Start */}

              <Card className="mb-24">
                <Card.Header className="border-bottom-0 d-flex justify-content-between">
                  <div className="text-header">
                    Your estimated total {toLowerCase(payFrequency)} deductions
                  </div>
                  <div className="text-larger font-weight-bold">
                    {formatCurrency(totalPackagePerPay)}
                  </div>
                </Card.Header>
                <Card.Body>
                  <AccordionSection
                    readerText={`Your estimated total ${toLowerCase(
                      payFrequency
                    )} deductions`}
                    expandAccordion={expandAccordion}
                    title="This amount covers everything from the car’s finance to your running costs and is estimated based on an annualised figure."
                  >
                    <>
                      <p className="font-weight-bold">
                        Why your package is more valuable than the car
                      </p>
                      <p className="font-color-indicative">
                        Your running costs, finance arrangement and the car
                        itself are all packaged up for you in your deductions.
                        Meaning you can hit the road with the peace of mind a
                        convenient budget brings. Plus, with the ease of novated
                        leasing, you'll enjoy a smoother on road experience.{' '}
                      </p>
                      <p className="font-weight-bold">Your total package</p>
                      <Row>
                        <Col className="col--nobold" xs={7} md={9}>
                          {isGov && displayInterest
                            ? `Car lease payments (includes ${annualRate}% interest)`
                            : `Car lease payments`}
                        </Col>
                        <Col xs={5} md={3} className="text-right">
                          {formatCurrency(carRepaymentPerPay)}
                        </Col>
                      </Row>
                      <Row>
                        <Col className="col--nobold">On the road budget</Col>
                        <Col className="text-right">
                          {formatCurrency(runningCostsPerPay)}
                        </Col>
                      </Row>
                      <Row>
                        {!!fuelBudget && (
                          <Col xs={12} md={6} className="mb-12 col--nobold">
                            <Icon iconType="Fuel" size={1.4} primary />
                            <span className="pl-14">Fuel</span>
                          </Col>
                        )}
                        {!!electricityBudget && (
                          <Col xs={12} md={6} className="mb-12 col--nobold">
                            <Icon iconType="Electricity" size={1.4} primary />
                            <span className="pl-14">Electricity</span>
                          </Col>
                        )}
                        {!!scheduleMaintenanceBudget && (
                          <Col xs={12} md={6} className="mb-12">
                            <Icon
                              iconType="ScheduledMaintenance"
                              size={1.4}
                              primary
                            />
                            <span className="pl-14">Scheduled servicing</span>
                          </Col>
                        )}
                        {!!tyresBudget && (
                          <Col xs={12} md={6} className="mb-12">
                            <Icon
                              iconType="ReplacementTyres"
                              size={1.4}
                              primary
                            />
                            <span className="pl-14">Replacement tyres</span>
                          </Col>
                        )}
                        {!!roadsideAssistanceBudget && (
                          <Col xs={12} md={6} className="mb-12">
                            <Icon
                              iconType="RoadsideAssistance"
                              size={1.4}
                              primary
                            />
                            <span className="pl-14">Roadside assistance</span>
                          </Col>
                        )}
                        {!!incidentalsBudget && (
                          <Col xs={12} md={6} className="mb-12">
                            <Icon iconType="Incidentals" size={1.4} primary />
                            <span className="pl-14">Incidentals</span>
                          </Col>
                        )}
                        {!!compInsRenewalBudget && (
                          <Col xs={12} md={6} className="mb-12">
                            <Icon
                              iconType="ComprehensiveInsurance"
                              size={1.4}
                              primary
                            />
                            <span className="pl-14">
                              Comprehensive insurance
                            </span>
                          </Col>
                        )}
                        {!!registrationBudget && (
                          <Col xs={12} md={6} className="mb-12">
                            <Icon iconType="Registration" size={1.4} primary />
                            <span className="pl-14">Annual registration</span>
                          </Col>
                        )}
                      </Row>
                      <Row className="text-right font-weight-bold border-top mx-0 pt-2 mt-3">
                        <Col xs md={9} className="px-0">
                          Total package deduction
                        </Col>
                        <Col xs md={3} className="px-0 text-right--bold">
                          {formatCurrency(totalPackagePerPay)}
                        </Col>
                      </Row>
                    </>
                  </AccordionSection>
                </Card.Body>
              </Card>
              {/* What we'll deduct each fortnight: End */}

              {/* How we help you do more with your salary: Start */}
              <Card className="mb-24">
                <Card.Header className="border-bottom-0 d-flex justify-content-between">
                  <div className="text-header">
                    Your Resulting take home pay
                  </div>
                  <div className="text-larger font-weight-bold">
                    {formatCurrency(takeHomePerPay)}
                  </div>
                </Card.Header>
                <Card.Body>
                  <AccordionSection
                    readerText="Your resultaing take home pay"
                    expandAccordion={expandAccordion}
                    title="Unpack how your chosen leasing method is estimated to affect your take home pay."
                    hasTabs
                  >
                    <>
                      <p className="font-color-indicative">
                        We'll deduct your payments from your salary before and
                        after it gets taxed (your gross income, and your
                        resulting net income). These pre-tax deductions could
                        free up money you're currently paying in income tax (and
                        the{' '}
                        <Tooltip
                          header="Medicare levy"
                          launchViaTitleText="true"
                          text="The Medicare levy is 2% of your taxable income, in addition to the tax you pay on your taxable income. It’s used to help fund some of the costs of Australia's public health system known as Medicare."
                        />
                        ), which we then put straight towards your lease.
                      </p>
                      <p className="font-weight-bold">Here are the numbers:</p>
                      <Tabs
                        data={[
                          {
                            title: payFrequency,
                            content: () => (
                              <TakeHomePayTable
                                grossSalary={grossSalaryPerPay}
                                preTaxDeduction={preTaxPerPay}
                                otherPreTax={otherPreTaxPerPay}
                                taxShare={taxSharePerPay}
                                netSalary={netSalaryPerPay}
                                taxMedicare={taxMedicarePerPay}
                                netIncome={netIncomePerPay}
                                postTax={postTaxPerPay}
                                otherPostTax={otherPostTaxPerPay}
                                takeHome={takeHomePerPay}
                                fbt={fbtPerPay}
                              />
                            ),
                          },
                          {
                            title: 'Annually',
                            content: () => (
                              <TakeHomePayTable
                                grossSalary={grossAnnualSalary}
                                preTaxDeduction={spPreTaxDeduction}
                                otherPreTax={preTaxDeduction}
                                taxShare={spTaxShareDeduction}
                                netSalary={netSalaryPerAnnum}
                                taxMedicare={spTaxAndMedicare}
                                netIncome={spNetCashSalary}
                                postTax={specm}
                                otherPostTax={postTaxDeduction}
                                takeHome={spCashinhand}
                                fbt={fbtPerAnnum}
                              />
                            ),
                          },
                        ]}
                        activeIndex={0}
                        equalWidth
                      />
                      <p className="my-4 text-small">
                        Disclaimer: The pre-tax salary in the calculations is
                        net of your employer’s compulsory superannuation
                        guarantee contribution. The Tax and Medicare
                        calculations do not take into account any HECS/HELP
                        repayments that may apply to your personal financial
                        circumstances.
                        {fbtValuationText && (
                          <span
                            className="font-weight-bold"
                            id="summarysection.fbtvaluation"
                          >
                            {fbtValuationText}
                          </span>
                        )}
                      </p>
                      <p className="font-weight-bold text-brand mb-2">
                        Want a second opinion from someone you trust?
                      </p>
                      <p className="mb-3 font-color-indicative">
                        {consultantFirstName} is here to explain the ins and
                        outs of your estimate to the people you turn to when
                        making decisions.
                      </p>
                      {applicationRole &&
                        applicationRole !== applicationRoles.CONSULTANT && (
                          <p className="font-weight-bold">
                            <ContactUsDialog
                              className="link"
                              theme="link"
                              launchText="Contact my consultant"
                              inlineText
                            />
                          </p>
                        )}
                      {applicationRole &&
                        applicationRole === applicationRoles.CONSULTANT && (
                          <p className="font-weight-bold">
                            <PrimaryLink disabled>
                              Contact my consultant
                            </PrimaryLink>
                          </p>
                        )}
                    </>
                  </AccordionSection>
                </Card.Body>
              </Card>
              {/* How we help you do more with your salary: End */}
              {gstSavingsOnRunningCosts ? (
                <Card className="mb-24">
                  <Card.Body className="border-bottom-0 d-flex justify-content-between">
                    <div className="text-small">
                      Your estimated GST savings on running costs
                    </div>
                    <div className="text-right--bold">
                      {formatCurrency(gstSavingsOnRunningCosts)}
                    </div>
                  </Card.Body>
                </Card>
              ) : null}
              <Card className="mb-24">
                <Card.Body className="border-bottom-0 d-flex justify-content-between">
                  <div className="text-small">
                    Your estimated life of lease savings
                  </div>
                  <div className="text-right--bold">
                    {formatCurrency(lifeOfLeaseTaxSaving)}
                  </div>
                </Card.Body>
              </Card>
              <Card>
                <Card.Body className="border-bottom-0 d-flex justify-content-between">
                  <div
                    onClick={ScrollToEndOfLeaseSection}
                    className="clickable mr-2 text-small"
                  >
                    Your total residual value (after lease term)
                  </div>
                  <div className="text-right--bold">
                    {formatCurrency(leaseResidualAmountIncGST)}
                  </div>
                </Card.Body>
              </Card>
            </Card.Body>
          </Card>
        </Container>
        <Container className="section mb-2 print-page-break-before">
          <AccordionCard
            expandAccordion={expandAccordion}
            title="Can I pay out my lease early?"
            className="card-shadow"
          >
            <div>
              <div>
                Yep, you can pay out your lease early and exit at any time.
                Generally speaking, we wouldn't recommend going down that path.
                If you do decide to, here are your options:
              </div>
              <ol>
                <li>
                  Buy the car outright, paying out whatever's owing on your
                  lease, plus your residual.
                </li>
                <li>
                  Return the car, paying out whatever's owing on your lease.
                </li>
              </ol>
              <div>
                Keep in mind whichever option you choose, you'll be paying back
                your lease in its entirety. e.g. If you want to exit a five year
                lease after only four years, you'll still have to pay out the
                full five years. And depending on how far into your lease you
                are, the car might not be worth as much as the amount you need
                to pay out.
              </div>
            </div>
          </AccordionCard>
        </Container>
        <Container className="section mb-4">
          <AccordionCard
            expandAccordion={expandAccordion}
            title="What if I leave my current job?"
            className="card-shadow"
          >
            <div>
              <div>
                You’ve always got options if you leave your current job.
              </div>
              <ol>
                <li>
                  {' '}
                  Buy the car outright, paying out whatever's owing on your
                  lease, plus your residual.
                </li>
                <li>
                  Keep paying for your lease yourself, with regular post-tax
                  dollars.
                </li>
                <li>
                  Transfer your lease to your new employer, subject to their
                  approval.
                  <br />
                  <div>
                    Taking out Lease Protection Insurance sets you up with some
                    security in case you're fired or made redundant. You'll need
                    to take this out before your lease begins.
                  </div>
                </li>
              </ol>
            </div>
          </AccordionCard>
        </Container>
      </Container>
    </div>
  )
}

export default SummarySection
